/**
 * Drawer component based on Vaul library.
 * Supports bottom (portrait) and right (landscape) directions.
 * @module drawer
 */

import * as React from 'react';
import { Drawer as DrawerPrimitive } from 'vaul';
import { cn } from '@/lib/utils';

/**
 * Root drawer component wrapper.
 */
const Drawer = ({
  shouldScaleBackground = false,
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Root>) => (
  <DrawerPrimitive.Root shouldScaleBackground={shouldScaleBackground} {...props} />
);
Drawer.displayName = 'Drawer';

const DrawerTrigger = DrawerPrimitive.Trigger;
const DrawerPortal = DrawerPrimitive.Portal;
const DrawerClose = DrawerPrimitive.Close;

/**
 * Drawer overlay with backdrop blur.
 */
const DrawerOverlay = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Overlay
    ref={ref}
    className={cn('fixed inset-0 z-50 bg-black/60 backdrop-blur-sm', className)}
    {...props}
  />
));
DrawerOverlay.displayName = 'DrawerOverlay';

/**
 * Drawer handle styled as a frog tongue tip.
 * Pink/salmon color, wider than default, pill-shaped.
 */
const DrawerHandle = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'mx-auto mt-4 h-2 w-[40px] rounded-full bg-pink-300',
      className
    )}
    {...props}
  />
));
DrawerHandle.displayName = 'DrawerHandle';

/**
 * Props for DrawerContent with direction support.
 */
interface DrawerContentProps
  extends React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Content> {
  /** Direction the drawer slides from */
  direction?: 'bottom' | 'right';
}

/**
 * Drawer content container with themed styling.
 * Supports bottom and right directions for portrait/landscape.
 */
const DrawerContent = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Content>,
  DrawerContentProps
>(({ className, children, direction = 'bottom', ...props }, ref) => (
  <DrawerPortal>
    <DrawerOverlay />
    <DrawerPrimitive.Content
      ref={ref}
      className={cn(
        // Base styles — z-51 so content sits above the z-50 overlay for desktop pointer events
        'fixed z-[51] flex flex-col bg-card',
        // Theme border and glow — frog mouth edge: green top border + rounded top corners
        'border border-primary/30 shadow-[0_0_20px_hsl(var(--glow)/0.15)]',
        // Bottom drawer (portrait)
        direction === 'bottom' && [
          'inset-x-0 bottom-0',
          'mt-24',
          'max-h-[85vh]',
          // Frog mouth curved edge: rounded top, green accent border
          '[border-radius:20px_20px_0_0] border-t-[3px] border-t-primary'
        ],
        // Right drawer (landscape)
        direction === 'right' && [
          'inset-y-0 right-0',
          'w-80 rounded-l-xl',
          'border-r-0'
        ],
        className
      )}
      {...props}
    >
      {direction === 'bottom' && <DrawerHandle />}
      {children}
    </DrawerPrimitive.Content>
  </DrawerPortal>
));
DrawerContent.displayName = 'DrawerContent';

/**
 * Drawer header section.
 */
const DrawerHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn('grid gap-1.5 p-4 text-center sm:text-left', className)}
    {...props}
  />
);
DrawerHeader.displayName = 'DrawerHeader';

/**
 * Drawer footer section.
 */
const DrawerFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn('mt-auto flex flex-col gap-2 p-4', className)}
    {...props}
  />
);
DrawerFooter.displayName = 'DrawerFooter';

/**
 * Drawer title.
 */
const DrawerTitle = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Title
    ref={ref}
    className={cn('text-lg font-semibold text-foreground', className)}
    {...props}
  />
));
DrawerTitle.displayName = 'DrawerTitle';

/**
 * Drawer description text.
 */
const DrawerDescription = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Description
    ref={ref}
    className={cn('text-sm text-muted-foreground', className)}
    {...props}
  />
));
DrawerDescription.displayName = 'DrawerDescription';

export {
  Drawer,
  DrawerPortal,
  DrawerOverlay,
  DrawerTrigger,
  DrawerClose,
  DrawerContent,
  DrawerHandle,
  DrawerHeader,
  DrawerFooter,
  DrawerTitle,
  DrawerDescription
};
